import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Drawer, Flex, MultiSelect } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Select, TextInput } from 'react-hook-form-mantine';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { useRelatedPeopleDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleDrawer/hooks/useRelatedPeopleDrawer.ts';
import {
	NamesOfPersonWithRelation,
	PersonRelationType,
	PersonType,
} from '@dealstack/shared/lib/generated';
import { RELATED_PEOPLE_FORM_FIELDS } from './utils/types.ts';
import { makeRelationTypeOptions } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/utils/relationTypes.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import {
	makeOptionWithTestId,
	makeOptionWithTestIdByValue,
} from '@dealstack/frontend/shared/components/Select/withOptionTestId';
import { SelectedRelatedPeopleList } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleDrawer/SelectedRelatedPeopleList.tsx';
import { PersonAddedAlert } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleDrawer/components/PersonAlert.tsx';
import { LegalRepresentativeFields } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleDrawer/components/LegalRepresentativeFields.tsx';

// Relation options are keyed by value: their labels are translated and vary by
// related-person type. Person options keep the label (display names are stable).
const RelationOption = makeOptionWithTestIdByValue(
	TEST_ELEMENT_IDS.CAP_RELATED_PEOPLE_DRAWER_RELATION_OPTION
);
const PersonOption = makeOptionWithTestId(TEST_ELEMENT_IDS.CAP_RELATED_PEOPLE_DRAWER_PERSON_OPTION);

interface Props {
	relatedPeople: NamesOfPersonWithRelation[] | null;
	relationType: PersonRelationType | null;
	selectedEntityType?: PersonType;
	hasPermission: boolean;
	opened: boolean;
	onClose: () => void;
}

export const RelatedPeopleDrawer = ({
	relatedPeople,
	hasPermission,
	opened,
	onClose,
	relationType,
	selectedEntityType,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const {
		relatedPeopleForm,
		onFormSubmit,
		isLoading,
		availablePeople,
		selectedRelatedPeopleIds,
		selectedRelationType,
		selectedEntityName,
	} = useRelatedPeopleDrawer(onClose);

	const [lastAddedPersonName, setLastAddedPersonName] = useState<string | null>(null);

	const relationTypeOptions = makeRelationTypeOptions(tCapManage, selectedEntityType);

	const availablePeopleOptions = useMemo(
		() =>
			(availablePeople ?? [])
				// Picked people live in the list below, never as a pill in the input, so
				// drop them from the picker options entirely.
				.filter((p) => !selectedRelatedPeopleIds?.includes(p.id))
				.map((p) => ({
					label: p.displayName ?? '',
					value: p.id,
				})),
		[availablePeople, selectedRelatedPeopleIds]
	);

	const selectedRelatedPeopleDisplayNames = useMemo(() => {
		const ids = selectedRelatedPeopleIds ?? [];
		if (!ids.length) {
			return '';
		}

		return ids
			.map((id) => availablePeople?.find((p) => p.id === id)?.displayName ?? '')
			.filter(Boolean)
			.join(', ');
	}, [selectedRelatedPeopleIds, availablePeople]);

	useEffect(() => {
		if (!opened) {
			relatedPeopleForm.reset({});
		}
	}, [opened]);

	useEffect(() => {
		relatedPeopleForm.reset({
			...(relationType ? { relation_type: relationType } : {}),
			person_ids: relatedPeople?.map((p) => p.id),
			[RELATED_PEOPLE_FORM_FIELDS.create_legal_rep]: true,
		});
	}, [relatedPeople, relationType]);

	const removePersonFromSelection = (personId: string) => {
		const remainingPersonIds = selectedRelatedPeopleIds.filter((id) => id !== personId);
		relatedPeopleForm.setValue(RELATED_PEOPLE_FORM_FIELDS.person_ids, remainingPersonIds);
	};
	const handlePersonIdsOnChange = (value: string[]) => {
		// The MultiSelect is kept value-less (no pills), so onChange reports only the
		// newly clicked option. Append it to person_ids — the selection then renders in
		// the list below and drops out of the picker options.
		const lastAddedPersonId = value[value.length - 1];
		if (!lastAddedPersonId || selectedRelatedPeopleIds?.includes(lastAddedPersonId)) {
			return;
		}
		const name =
			availablePeopleOptions.find(({ value }) => value === lastAddedPersonId)?.label ?? null;
		setLastAddedPersonName(name);
		setTimeout(() => setLastAddedPersonName(null), 3000);

		relatedPeopleForm.setValue(RELATED_PEOPLE_FORM_FIELDS.person_ids, [
			...(selectedRelatedPeopleIds ?? []),
			lastAddedPersonId,
		]);
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={
				relatedPeople?.length
					? tCapManage('related_people.drawer.edit_title')
					: tCapManage('related_people.drawer.add_title')
			}
		>
			<FormProvider {...relatedPeopleForm}>
				<Flex direction="column" justify="space-between" h="100%">
					<Flex direction="column" gap="1rem">
						<Select
							name={RELATED_PEOPLE_FORM_FIELDS.relation_type}
							control={relatedPeopleForm.control}
							label={tCapManage('related_people.drawer.form.relation_type_label')}
							placeholder={tCapManage(
								'related_people.drawer.form.relation_type_placeholder'
							)}
							data={relationTypeOptions}
							renderOption={RelationOption}
							data-testid={TEST_ELEMENT_IDS.CAP_RELATED_PEOPLE_DRAWER_RELATION_SELECT}
							rightSection={<Icon variant="caret-down" size="lg" />}
							styles={{ rightSection: { pointerEvents: 'none' } }}
							searchable
							size="md"
							disabled={!hasPermission || !!relationType}
						/>
						{selectedRelationType === PersonRelationType.OTHER && (
							<TextInput
								name={RELATED_PEOPLE_FORM_FIELDS.custom_relation_type}
								control={relatedPeopleForm.control}
								label={tCapManage(
									'related_people.drawer.form.custom_relation_type_label'
								)}
								placeholder={tCapManage(
									'related_people.drawer.form.custom_relation_type_placeholder'
								)}
								data-testid={
									TEST_ELEMENT_IDS.CAP_RELATED_PEOPLE_DRAWER_CUSTOM_ROLE_INPUT
								}
								size="md"
								disabled={!hasPermission}
							/>
						)}
						<MultiSelect
							data={availablePeopleOptions}
							renderOption={PersonOption}
							data-testid={TEST_ELEMENT_IDS.CAP_RELATED_PEOPLE_DRAWER_PERSON_SELECT}
							name={RELATED_PEOPLE_FORM_FIELDS.person_ids}
							// Kept empty so picks never render as pills here; they appear in
							// the Related people list below instead.
							value={[]}
							onChange={handlePersonIdsOnChange}
							label={
								<Flex align="center" justify="space-between" gap="0.25rem">
									{tCapManage('related_people.drawer.form.person_name_label')}
									{lastAddedPersonName && (
										<PersonAddedAlert personName={lastAddedPersonName} />
									)}
								</Flex>
							}
							labelProps={{
								style: { width: '100%' },
							}}
							placeholder={tCapManage(
								'related_people.drawer.form.person_name_placeholder'
							)}
							disabled={!availablePeople?.length}
							rightSection={<Icon variant="caret-down" size="lg" />}
							styles={{ rightSection: { pointerEvents: 'none' } }}
							size="md"
							clearable
							multiple
							searchable
						/>

						{selectedRelationType === PersonRelationType.PIV &&
							selectedRelatedPeopleIds?.length > 0 && (
								<LegalRepresentativeFields
									disabled={!hasPermission}
									naturalPersonName={
										selectedEntityType === PersonType.NATURAL
											? selectedEntityName
											: selectedRelatedPeopleDisplayNames
									}
									legalEntityName={
										selectedEntityType === PersonType.NATURAL
											? selectedRelatedPeopleDisplayNames
											: selectedEntityName
									}
								/>
							)}

						<SelectedRelatedPeopleList
							availablePeople={availablePeople ?? []}
							relationType={selectedRelationType}
							relatedPeopleIds={selectedRelatedPeopleIds}
							unselectPerson={removePersonFromSelection}
							hasEditPermission={hasPermission}
						/>
					</Flex>

					<Button
						onClick={relatedPeopleForm.handleSubmit(onFormSubmit)}
						disabled={isLoading}
						data-testid={TEST_ELEMENT_IDS.CAP_RELATED_PEOPLE_DRAWER_SUBMIT_BUTTON}
					>
						{relatedPeople?.length
							? tCapManage('related_people.drawer.edit_button')
							: tCapManage('related_people.drawer.add_button')}
					</Button>
				</Flex>
			</FormProvider>
		</Drawer>
	);
};
